import { createContext, useContext } from 'react';
import type { PassengerRideView, RideStopView } from '@limuzin/passenger';

/**
 * The order being composed, ported from `apps/rider/lib/features/booking/draft.dart`.
 *
 * It lives above the screen so that opening the address picker, wandering off to «Мои
 * адреса» and coming back, or being interrupted by a ride starting, does not lose it.
 */

export type When = 'now' | 'today' | 'tomorrow';

export interface Draft {
  from: string;
  /**
   * Everything after the pickup. The last one is «Куда»; the rest are stops on the
   * way. Never empty — an order with no destination row cannot be drawn.
   */
  stops: string[];
  comment: string;
  when: When;
  hour: number;
  minute: number;
}

export const emptyDraft: Draft = {
  from: '',
  stops: [''],
  comment: '',
  when: 'now',
  hour: 19,
  minute: 0,
};

interface DraftContext {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
  /** The draft has gone out as this «Сейчас» ride, and stays until it is under way. */
  sent: (rideId: string) => void;
}

export const DraftCtx = createContext<DraftContext | null>(null);

export function useDraft(): DraftContext {
  const value = useContext(DraftCtx);
  if (!value) throw new Error('useDraft outside DraftCtx');
  return value;
}

/**
 * Whether the draft that went out as `sentAs` is done with: the ride on «Заказ» is
 * that one, and the passenger is in the car. Until then the draft outlives the
 * «Сейчас» order it became — «Ищем машину» and every screen after it stand where the
 * form was, and a passenger who comes back to the form from one (cancelling,
 * «Машину не нашли», a driver who gave the ride up) finds what they typed there
 * rather than an empty page.
 *
 * A предзаказ clears the draft on the tap instead: it goes to «Запланированные» and
 * leaves the passenger on the form, where an order still standing in it would read
 * as one that had not gone.
 */
export const aboard = (ride: PassengerRideView | null, sentAs: string | null): boolean =>
  ride !== null &&
  ride.id === sentAs &&
  (ride.status === 'IN_PROGRESS' || ride.status === 'COMPLETED');

/**
 * Where the car is wanted is the whole requirement. «Куда» is not: a passenger
 * standing in the road knows where they are long before they have settled on where
 * they are going, and telling the driver in the car is how that has always been done
 * here. The server fills the destination in as unnamed when it is missing.
 */
export const isComplete = (draft: Draft): boolean => draft.from.trim().length > 0;

/** The form as nobody has touched it: nothing typed, and «Сейчас». */
export const isBlank = (draft: Draft): boolean =>
  draft.from.trim() === '' &&
  draft.stops.every((stop) => stop.trim() === '') &&
  draft.comment.trim() === '' &&
  draft.when === 'now';

/**
 * Blank rows are dropped, and whatever survives last is «Куда» — so a route whose
 * destination row is empty but whose stop is filled still names a destination. A
 * pickup on its own is a whole order too.
 */
export function toStops(draft: Draft): RideStopView[] {
  const named = draft.stops.map((raw) => raw.trim()).filter(Boolean);
  return [
    { kind: 'PICKUP', address: draft.from.trim() },
    ...named.map((address, index) => ({
      kind: index === named.length - 1 ? ('DESTINATION' as const) : ('STOP' as const),
      address,
    })),
  ];
}

/**
 * Any row can be emptied, «Откуда» included — a passenger who picked the wrong place
 * starts that row over without having to pick another first. `index` counts the
 * route as it is drawn: 0 is «Откуда».
 */
export function clearRow(draft: Draft, index: number): Partial<Draft> {
  if (index === 0) return { from: '' };
  return { stops: draft.stops.map((stop, i) => (i === index - 1 ? '' : stop)) };
}

/**
 * A new stop is a blank row just above «Куда», which stays where it was: a passenger
 * adds a stop on the way to somewhere already chosen, and a new row at the bottom
 * would push that destination up into a stop and leave «Куда» empty.
 */
export function addStop(draft: Draft): Partial<Draft> {
  return { stops: [...draft.stops.slice(0, -1), '', ...draft.stops.slice(-1)] };
}

/**
 * The whole route reorders, the pickup included: a passenger who typed where they
 * are going into the first row drags it down rather than retyping both. Indices
 * count the route as it is drawn — 0 is «Откуда» — and whatever lands first becomes
 * the pickup, whatever lands last becomes «Куда».
 */
export function reorderRoute(draft: Draft, from: number, to: number): Partial<Draft> {
  const route = [draft.from, ...draft.stops];
  if (from === to || from < 0 || from >= route.length) return {};
  const [moved] = route.splice(from, 1);
  if (moved === undefined) return {};
  route.splice(Math.max(0, Math.min(to, route.length)), 0, moved);
  return { from: route[0] ?? '', stops: route.slice(1) };
}

/**
 * Null for «Сейчас». Otherwise today or tomorrow at the chosen time, which is what
 * the server stores and what dispatch opens ahead of, as the panel says.
 */
export function scheduledFor(draft: Draft, now = new Date()): Date | null {
  if (draft.when === 'now') return null;
  const day = new Date(now);
  if (draft.when === 'tomorrow') day.setDate(day.getDate() + 1);
  day.setHours(draft.hour, draft.minute, 0, 0);
  return day;
}

/**
 * What the server puts in the destination row of an order placed without «Куда» —
 * `UNNAMED_DESTINATION` in `server/src/places/places.service.ts`, word for word. It
 * is a sentence for the route card to show, not somewhere to go.
 */
export const UNNAMED_DESTINATION = 'Адрес назовёт пассажир';

/**
 * Whether this row names somewhere. The unnamed destination does not, so nothing
 * that collects addresses — «Другие адреса», «Повторить» — takes it.
 */
export const isNamed = (stop: RideStopView): boolean =>
  stop.address.trim() !== UNNAMED_DESTINATION;

/**
 * «Повторить» — the same route again, right now. A ride ordered without «Куда» comes
 * back without one: the row is left blank, not filled with the sentence the server
 * stood in it.
 */
export function repeat(stops: RideStopView[]): Partial<Draft> {
  const from = stops.find((s) => s.kind === 'PICKUP') ?? stops[0];
  const middle = stops.filter((s) => s.kind === 'STOP').map((s) => s.address);
  const to = stops[stops.length - 1];
  return {
    from: from?.address ?? '',
    stops: [...middle, to && isNamed(to) ? to.address : ''],
    when: 'now',
  };
}
